'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import SvgChevronright from '@common/icon/SvgChevronright';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { CustomLink } from '@/app/_components/CustomLink';
import { DONATION_ROUTES } from '@/constants';
import type { PartnersQueries } from '../../_utils/queries';
import { contextOf } from '../../_utils/queries';
import type { ReportFilter } from '../../_utils/report/report';
import { filterToLinkQuery, filterToSearchParams, rangeLabelOf } from '../../_utils/report/report';
import type { PartnersLinkStats } from '../../_utils/types';
import { LinkCard } from '../link-card/LinkCard';
import { SectionLoadError } from '../SectionLoadError';
const PREVIEW_COUNT = 3;
interface ContentsPreviewBodyProps {
  contents: PartnersLinkStats[] | null;
  filter: ReportFilter;
}
const ContentsPreviewBody = t0 => {
  const $ = _c(12);
  const {
    contents,
    filter
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="text-Gray-900">내 콘텐츠</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = contents ? contents.length : "-";
  let t3;
  if ($[1] !== t2) {
    t3 = <h3 className="flex gap-4 font-700 typo-body3">{t1}<span className="text-B-550">{t2}개</span></h3>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== contents?.length) {
    t4 = contents?.length === 0 ? <p className="flex flex-col items-center gap-4 break-keep rounded-small bg-Gray-25 px-20 py-28 text-center typo-body4"><span className="font-600 text-Gray-700">콘텐츠별 실적이 아직 없어요</span><span className="text-Gray-550">링크를 콘텐츠에 넣으면 콘텐츠마다 실적을 볼 수 있어요.</span></p> : null;
    $[3] = contents?.length;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== contents || $[6] !== filter) {
    t5 = contents && contents.length > 0 ? <div className="flex flex-col gap-12"><ul className="flex flex-col gap-12">{contents.slice(0, PREVIEW_COUNT).map(link => <li key={link.partnersLinkId}><LinkCard link={link} rangeLabel={rangeLabelOf(filter)} /></li>)}</ul><Button asChild={true} theme="sub-line" dimensions="H48"><CustomLink href={`${DONATION_ROUTES.PARTNERS_CONTENTS.pathname}?${filterToSearchParams(filter)}`}>더보기<SvgChevronright aria-hidden={true} className="size-16" /></CustomLink></Button></div> : null;
    $[5] = contents;
    $[6] = filter;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = <section className="flex flex-col gap-12">{t3}{t4}{t5}</section>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
interface ContentsPreviewProps {
  queries: PartnersQueries;
  filter: ReportFilter;
}
const ContentsPreviewData = t0 => {
  const $ = _c(8);
  const {
    queries,
    filter
  } = t0;
  let t1;
  if ($[0] !== filter || $[1] !== queries) {
    t1 = queries.links(filterToLinkQuery(filter));
    $[0] = filter;
    $[1] = queries;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = {
      ...t1,
      select: contextOf
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data
  } = useSuspenseQuery(t2);
  let t3;
  if ($[5] !== data.contents || $[6] !== filter) {
    t3 = <ContentsPreviewBody contents={data.contents} filter={filter} />;
    $[5] = data.contents;
    $[6] = filter;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
export const ContentsPreview = props => {
  const $ = _c(8);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SectionLoadError />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.filter) {
    t1 = <ContentsPreviewBody contents={null} filter={props.filter} />;
    $[1] = props.filter;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props) {
    t2 = <ContentsPreviewData {...props} />;
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <ErrorBoundary fallback={t0}><Suspense fallback={t1}>{t2}</Suspense></ErrorBoundary>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};